import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown, MenuProps } from "antd";
import config from "./../../config";
import {
  LogoutOutlined,
  SettingOutlined,
  BarsOutlined,
  PrinterOutlined,
  ShopOutlined,
  OrderedListOutlined,
  CopyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  UtensilsCrossed,
  ListOrdered,
  TextAlignJustify,
  Banknote,
} from "lucide-react";
import SettingsView from "./Settings/Settings";
import PrinterSettingsView from "./Settings/PrinterSettingsView";
import "./less/_main-dir.less";
import GenereateOrderNowView from "./Generate/GenereateOrderNowView";
// service
import LogService from "../../services/api/LogService";
// utils
import DialogUtils from "../../utils/DialogUtils";
// redux
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { orderReset } from "../../store/slices/orderSlice";

const NewHeaderView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const orderDetails = useAppSelector((state) => state.order);

  const [store, setStore] = useState("");
  // const [user, setUser] = useState({ first_name: "", last_name: "" });
  const [settingsModalVisible, setsettingsModalVisible] = useState(false);
  const [printerSettingsModalVisible, setPrinterSettingsModalVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);

  const logService = new LogService();

  useEffect(() => {
    const storeString = localStorage.getItem("store") ?? "{}";
    const storObj = JSON.parse(storeString);

    // const userString = localStorage.getItem("user") ?? "{}";
    // const userObj = JSON.parse(userString);

    if (storObj) setStore(storObj.name);
    // if (userObj) setUser(userObj);
  }, [location.pathname]);

  const onLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("store");
    navigate("/");
  };

  const onClickSettings = () => setsettingsModalVisible(true);
  const onClickGenerate = () => setGenerateModalVisible(true);
  const onClickPrinterSettings = () => setPrinterSettingsModalVisible(true);

  const onClose = () => {
    setsettingsModalVisible(false);
    setGenerateModalVisible(false);
  };

  const onPrinterSettingsClose = () => setPrinterSettingsModalVisible(false);

  const managementName = () => {
    switch (location.pathname) {
      case "/orders": return "Order Management";
      case "/menu": return "Menu";
      case "/restaurant_detail":
      case "/": return "Restaurant Details";
      case "/reservations": return "Reservations";
      case "/sales_history": return "Sales Order History";
      case "/receivable": return "Receivables";
      case "/profile": return "Profile";
      default: return null;
    }
  };

  const menu = async (data: any) => {
    if (
      data.key !== "orders" &&
      data.key !== "settings" &&
      data.key !== "printersettings"
    ) {
      dispatch(orderReset());
    }

    switch (data.key) {
      case "restaurant_details":
        navigate("/restaurant_detail");
        break;
      case "menu":
        navigate("/menu");
        break;
      case "reservations":
        navigate("/reservations");
        break;
      case "orders":
        navigate("/orders");
        break;
      case "sales_order_history":
        navigate("/sales_history");
        break;
      case "receivables":
        navigate("/receivable");
        break;
      case "profile":
        navigate("/profile");
        break;
      case "generateordernow": {
        // AP-5800: a merchant registers with a store name only — the store row
        // already has an _id and name before Restaurant Details (address, etc.)
        // are filled in. Without a complete address there is no usable store slug,
        // so the generated snippet/links point nowhere and the app ends on a
        // blank screen. Gate on the address fields the Restaurant Details form
        // itself requires.
        const storeString = localStorage.getItem("store") ?? "{}";
        const storObj = JSON.parse(storeString);
        const hasRestaurantDetails =
          storObj?._id &&
          storObj?.address_1 &&
          storObj?.city &&
          storObj?.state &&
          storObj?.zip_code &&
          storObj?.country;
        if (!hasRestaurantDetails) {
          DialogUtils.error("Please complete your Restaurant Details before generating an order.");
          break;
        }
        await logService.addLog([{ action: "Load Generate Order Now", data: {} }]);
        onClickGenerate();
        break;
      }
      case "settings":
        onClickSettings();
        break;
      case "printersettings":
        await logService.addLog([{ action: "Load Printer Setting", data: {} }]);
        onClickPrinterSettings();
        break;
      case "logout":
        onLogout();
        break;
      default:
        break;
    }
  };

  const activeKey = () => {
    switch (location.pathname) {
      case "/orders": return "orders";
      case "/menu": return "menu";
      case "/restaurant_detail":
      case "/": return "restaurant_details";
      case "/reservations": return "reservations";
      case "/sales_history": return "sales_order_history";
      case "/receivable": return "receivables";
      case "/profile": return "profile";
      default: return "";
    }
  };

  const navItems: MenuProps["items"] = [
    {
      type: "group",
      label: <span className="nav-group-title">{store}</span>,
      children: [
        { key: "restaurant_details", className: "modified-item", label: <div className="nav-item"><ShopOutlined /><span>Restaurant Details</span></div> },
        { key: "menu", className: "modified-item", label: <div className="nav-item"><ListOrdered size={14} /><span>Menu</span></div> },
        { key: "reservations", className: "modified-item", label: <div className="nav-item"><UtensilsCrossed size={14} /><span>Reservations</span></div> },
        { key: "orders", className: "modified-item", label: <div className="nav-item"><OrderedListOutlined /><span>Orders</span></div> },
        { key: "sales_order_history", className: "modified-item", label: <div className="nav-item"><TextAlignJustify size={14} /><span>Sales Order History</span></div> },
        { key: "receivables", className: "modified-item", label: <div className="nav-item"><Banknote size={16} /><span>Receivables</span></div> },
        { key: "generateordernow", className: "modified-item", label: <div className="nav-item"><CopyOutlined /><span>Generate Order Now</span></div> },
        { key: "settings", className: "modified-item", label: <div className="nav-item"><SettingOutlined /><span>Settings</span></div> },
        ...(config.IS_ENABLE_PRINTING && config.IS_ENABLE_PRINTING === "true"
          ? [{ key: "printersettings", className: "modified-item", label: <div className="nav-item"><PrinterOutlined /><span>Printer Settings</span></div> }]
          : []),
        { key: "profile", className: "modified-item", label: <div className="nav-item"><UserOutlined /><span>Profile</span></div> },
        { key: "logout", className: "modified-item", label: <div className="nav-item"><LogoutOutlined /><span>Log out</span></div> },
      ],
    },
  ];

  return (
    <div className="main_header">
      <div className="header_web_2">
        {localStorage.getItem("access_token") ? (
          <div className="header_2">
            <div className="left_header_view">
              <div className="menu_bar">
                <Dropdown
                  menu={{ items: navItems, onClick: menu, selectedKeys: [activeKey()] }}
                  trigger={["click"]}
                  placement="bottomLeft"
                  overlayClassName="nav-dropdown"
                  // AP-5833: snap the drawer flush to the left page edge (the
                  // trigger has 16px left padding) and drop it from the bottom of
                  // the 75px header instead of from the middle of the trigger, so
                  // header + drawer read as one panel.
                  align={{ offset: [-16, 28] }}
                >
                  <BarsOutlined className="nav-trigger" />
                </Dropdown>
              </div>
              <div className="management_name">
                <div>{managementName()}</div>
              </div>
            </div>

            {location.pathname === "/orders" && (
              <div className="delivery_order_type">
                {orderDetails.order_Type} Order Details
              </div>
            )}

            <div className="header__right">
              <div className="user_tab">
                <div className="appetit_logo">
                  <img src="img/logo.png" alt="appetit logo" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="header">
            <div className="header__left">
              <div className="header_tab extra"></div>
              <div className="header__logo">
                <a href="/"><img src="img/logo.png" alt="appetit logo" /></a>
                <div></div>
              </div>
              <div className="header_tab">
                <a href="/about">About</a>
              </div>
            </div>
            <div className="header__right">
              <div className="header_tab">
                <span>
                  <a href={import.meta.env.VITE_FE_CUSTOMER_URL}>
                    Book a reservation or place an order now!
                  </a>
                </span>
              </div>
              <div className="header_tab extra"></div>
            </div>
          </div>
        )}
      </div>

      {settingsModalVisible && (
        <SettingsView visible={settingsModalVisible} onCancel={onClose} />
      )}

      {printerSettingsModalVisible && (
        <PrinterSettingsView visible={printerSettingsModalVisible} onCancel={onPrinterSettingsClose} />
      )}

      {generateModalVisible && (
        <GenereateOrderNowView visible={generateModalVisible} onCancel={onClose} />
      )}
    </div>
  );
};

export default NewHeaderView;